import {useId, useState} from 'react'
import {StopIcon} from '@primer/octicons-react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash} from '@primer/react'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import type {
  StackSectionStatusType,
  StackSectionStackEntry,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {StackStatusIcon} from './stack-section/StatusIcon'
import {useDisableAutoMergeMutation} from '../../hooks/mutations/use-disable-auto-merge-mutation'
import {StackList} from './stack-section/StackList'

export function StackSection({
  pullRequestId,
  status,
  loadingEntryCount,
  baseRefName,
  stackEntries,
  isStackMergeInProgress = false,
  viewerCanDisableAutoMerge = false,
  shouldOnlyRenderStackList = false,
  stackMetadata,
  entriesBeforeCount,
  entriesToMergeCount,
  autoMergeRangePullRequestCount,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  loadingEntryCount: number
  baseRefName?: string
  stackEntries: StackSectionStackEntry[]
  isStackMergeInProgress?: boolean
  viewerCanDisableAutoMerge?: boolean
  // Hides the section header and rebase actions, leaving only the list of stacked pull requests in an always expanded container.
  shouldOnlyRenderStackList?: boolean
  stackMetadata?: PullRequestUserActionStack
  entriesBeforeCount: number
  entriesToMergeCount: number
  autoMergeRangePullRequestCount: number
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )
  const [cancelMergeError, setCancelMergeError] = useState<string | undefined>()

  const {mutate: disableAutoMerge, isPending: isCancelMergePending} = useDisableAutoMergeMutation({
    onError: (e: Error) => {
      setCancelMergeError(e.message)
    },
  })

  const handleCancelMerge = () => {
    if (isCancelMergePending) return
    setCancelMergeError(undefined)
    disableAutoMerge()
  }

  let title
  let subtitle
  let anyPullsAreLoading = false
  const isFirstUnmergedPRInStack = entriesBeforeCount === 0

  switch (true) {
    case isStackMergeInProgress && isFirstUnmergedPRInStack:
      title = 'Merging this pull request in the stack…'
      subtitle = 'This pull request is being merged.'
      break
    case isStackMergeInProgress:
      title = 'Merging stack…'
      subtitle = `${entriesToMergeCount} pull requests are being merged.`
      break
    case (status === 'FAILED' || status === 'BLOCKED') && isFirstUnmergedPRInStack:
      title = 'Unable to merge'
      subtitle = 'This pull request cannot be merged.'
      break
    case status === 'FAILED' || status === 'BLOCKED':
      title = 'Unable to merge as a stack'
      subtitle = 'Some of the pull requests in this stack cannot be merged.'
      break
    case status === 'PASSED' && isFirstUnmergedPRInStack:
      title = 'Able to merge this pull request'
      subtitle = 'Only this pull request will be merged.'
      break
    case status === 'PASSED':
      title = 'Able to merge as a stack'
      subtitle = `Merging this pull request will also merge ${entriesBeforeCount} pull request${entriesBeforeCount === 1 ? '' : 's'} below it.`
      break
    case status === 'LOADING_MERGE_STATE':
      anyPullsAreLoading = true
      title = 'Loading stack status...'
      subtitle = `${loadingEntryCount} of ${stackEntries.length} pull requests loading`
      break
    case status === 'PENDING':
      title = 'Waiting on status checks...'
      subtitle = 'Some of the pull requests in this stack have pending status checks.'
      break
    default:
      title = 'Could not determine stack status'
      subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={shouldOnlyRenderStackList ? undefined : stackSectionAriaId}
      className={`${styles.StackSection} border-bottom color-border-subtle`}
    >
      {!shouldOnlyRenderStackList && (
        <MergeBoxSectionHeader
          headerId={stackSectionAriaId}
          title={title}
          subtitle={subtitle}
          icon={
            anyPullsAreLoading || isStackMergeInProgress ? (
              <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
            ) : (
              <StackStatusIcon status={status} isFirstUnmergedPRInStack={isFirstUnmergedPRInStack} />
            )
          }
          {...(isStackMergeInProgress
            ? {
                rightSideContent: (
                  <Button
                    disabled={!viewerCanDisableAutoMerge}
                    onClick={handleCancelMerge}
                    loading={isCancelMergePending}
                    loadingAnnouncement="Cancelling stack merge"
                  >
                    Cancel merge
                  </Button>
                ),
              }
            : {
                expandableProps: {
                  ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
                  isExpanded: stackExpanded,
                  onToggle: () => {
                    setStackExpanded(!stackExpanded)
                  },
                },
              })}
        />
      )}
      <MergeBoxExpandable isExpanded={shouldOnlyRenderStackList || isStackMergeInProgress || stackExpanded}>
        <StackList
          baseRefName={baseRefName}
          currentPullRequestId={pullRequestId}
          headingLevel={shouldOnlyRenderStackList ? 'h3' : 'h4'}
          stackMetadata={stackMetadata}
          stackEntries={stackEntries}
          autoMergeRangePullRequestCount={autoMergeRangePullRequestCount}
        />
      </MergeBoxExpandable>
      {cancelMergeError && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {cancelMergeError}
        </Flash>
      )}
    </section>
  )
}
